import { analyticsPageObj, analyticsTicketObj, analyticsUserObj } from "./use-general-entities";
import { PageType } from "@/shared/model/enums/analytics/page";
import { useFavouritesStore } from "@/shared/stores/use-favourites-store";
import { useDrilldownNodesStore } from "@/shared/stores/use-drilldown-nodes-store";
import { AnalyticsBet, AnalyticsOutcome, AnalyticsTicket } from "@/shared/model/analytics/entities";
import { useTicketStore } from "@/ticket/stores/use-ticket-store";
import { BetLabel } from "@/shared/model/enums/analytics/bet";
import { KombiGroup, PriceChangeBehaviour, TicketType } from "@/shared/model/ticket";
import useUtils from "../use-utils";
import { Constants } from "@/shared/model/enums/analytics/constants";
import { OutcomeMarket } from "@/shared/model/outcome";
import { EmptyMarket } from "@/shared/model/market";
import {
    EventsDrilldownDetailEventMarket,
    EventsDrilldownDetailEventMarketOutcome,
} from "@/live/model/events-drilldown-detail-event";
import { BetData } from "@/shared/model/bet";
import useIsBreakpoint from "../use-is-breakpoint";
import { nextTick } from "vue";

export default function useAnalyticsUtils() {
    const { formatRate } = useUtils();
    const { isMobile } = useIsBreakpoint();

    const setAnalyticsFavouriteSports = async () => {
        const { getSportById } = useDrilldownNodesStore();
        const { favouriteSportIds } = useFavouritesStore();

        const favouriteSportsPromises = favouriteSportIds
            .filter((sport) => sport.present)
            .map(async (sport) => await getSportById(sport.id));

        try {
            const resolvedSports = await Promise.all(favouriteSportsPromises);
            const favouriteSports = resolvedSports.map((sport) => sport?.name).join(", ");
            analyticsPageObj.favoriteSports = favouriteSports;
        } catch (error) {
            console.error("Error resolving favourite sports:", error);
        }
    };

    const setPageAnalytics = async (to) => {
        if (to.name === "Homepage") {
            if (isMobile.value) {
                analyticsPageObj.category = "domu";
            } else {
                analyticsPageObj.category = "kurzy";
            }
        } else {
            const firstSegment = to.path.split("/").filter(Boolean)[0];
            analyticsPageObj.category = firstSegment || "other";
        }

        if (to.name === "LiveEventDetail" || to.name === "PrematchEventDetail" || to.name === "PrematchLeagueDetail") {
            analyticsPageObj.type = PageType.Detail;
        } else {
            analyticsPageObj.type = PageType.List;
        }

        if (to.name === "Search") {
            analyticsPageObj.type = PageType.ListSearch;
        }
        //clear previous data
        analyticsPageObj.kind = Constants.NotApplied;
        analyticsPageObj.gameCode = Constants.NoGame;
        analyticsPageObj.gameCodeStrict = Constants.NoGame;
    };

    const setTicketGeneral = async () => {
        nextTick(() => {
            const ticketStore = useTicketStore();

            if (ticketStore.ticketType === TicketType.KOMBI) {
                const kombiGroupNumbers = Object.keys(ticketStore.kombiGroupMapping);

                const getOutcomeIdsByGroupNumber = (groupNumber: string) => ticketStore.kombiGroupMapping[groupNumber];
                const groupRate = (groupNumber: string) => {
                    const outcomeIds = getOutcomeIdsByGroupNumber(groupNumber);
                    const rate = ticketStore.ticketOutcomes
                        ?.filter((o) => outcomeIds.includes(o.id))
                        ?.reduce((accumulator, outcome) => {
                            return accumulator * Number(outcome?.prices[0]?.decimal);
                        }, 1);
                    return rate > 1 ? formatRate(rate) : "";
                };

                const groupRates = kombiGroupNumbers
                    .map((groupNumber) => {
                        const rate = groupRate(groupNumber);
                        return `${KombiGroup[groupNumber]}:${rate}`;
                    })
                    .join(",");

                analyticsTicketObj.totalOdd = groupRates;
            } else if (isNaN(ticketStore.potentialPayout)) {
                analyticsTicketObj.totalOdd = Constants.NotApplied;
            } else {
                analyticsTicketObj.totalOdd = formatRate(ticketStore.potentialPayout).toString();
            }

            analyticsTicketObj.totalDeposit = ticketStore.stake.toString();
            analyticsTicketObj.type = ticketStore.ticketType;
            analyticsTicketObj.additionalWinnings = ticketStore.bonus
                ? (Math.round((ticketStore.bonus + Number.EPSILON) * 100) / 100).toString()
                : Constants.NoAdditionalWinnings;

            analyticsUserObj.showMiniTicket = ticketStore.optionDisplayMiniTicket;
            analyticsUserObj.oddSettings = renameTicketSettings(ticketStore.optionPriceChangeBehaviour);
            analyticsTicketObj.potentialWinnings = ticketStore.possibleWin.toString();
        });
    };

    const setTicketBetsGeneral = async () => {
        const { getLeagueById } = useDrilldownNodesStore();

        const analyticsBets: AnalyticsBet[] = [];
        const ticketStore = useTicketStore();
        const kombiActive = ticketStore.ticketType === TicketType.KOMBI;

        for (const outcome of ticketStore.originalOutcomes) {
            const groupNumber = ticketStore.kombiGroupNumberByOutcomeId(outcome.id)?.toString();
            const event = ticketStore.getEventByOutcomeId(outcome.id);
            if (!event) continue;
            const league = await getLeagueById(event.competitionDrilldownTagId);
            if (!league) continue;
            const bet: AnalyticsBet = {
                label: event.liveNow ? BetLabel.Live : BetLabel.Prematch,
                group: kombiActive ? KombiGroup[groupNumber] : undefined,
                gameCode: league.code,
                sportLeague: league.name,
                matchName: event.name,
                quote: outcome.prices[0].decimal.toString(),
                betPlacement: outcome.betPlacement,
                betCategory: outcome.betCategory,
                betType: outcome.betType,
            };
            analyticsBets.push(bet);
        }

        analyticsTicketObj.bets = analyticsBets;
        analyticsTicketObj.betCount = analyticsBets.length;
    };

    const getTicketBet = async (
        outcome: OutcomeMarket | EmptyMarket | EventsDrilldownDetailEventMarketOutcome,
    ): Promise<AnalyticsBet> => {
        const { getLeagueById } = useDrilldownNodesStore();

        const ticketStore = useTicketStore();
        const event = ticketStore.getEventByOutcomeId(outcome.id);
        const groupNumber = ticketStore.kombiGroupNumberByOutcomeId(outcome.id)?.toString();
        const kombiActive = ticketStore.ticketType === TicketType.KOMBI;
        const league = await getLeagueById(event?.competitionDrilldownTagId);

        if (!event) {
            return {
                label: BetLabel.Prematch,
                group: kombiActive ? KombiGroup[groupNumber] : undefined,
                gameCode: Constants.NotApplied,
                sportLeague: Constants.NotApplied,
                matchName: outcome.name,
                quote: outcome.prices[0].decimal.toString(),
                betPlacement: outcome.betPlacement,
                betCategory: outcome.betCategory,
                betType: outcome.betType,
            };
        }

        return {
            label: event.liveNow ? BetLabel.Live : BetLabel.Prematch,
            group: kombiActive ? KombiGroup[groupNumber] : undefined,
            gameCode: league.code,
            sportLeague: league.name,
            matchName: event.name,
            quote: outcome.prices[0].decimal.toString(),
            betPlacement: outcome.betPlacement,
            betCategory: outcome.betCategory,
            betType: outcome.betType,
        };
    };

    const getAnalyticsTicketBetFromPlacedTicket = (outcome: AnalyticsOutcome, kombi: boolean) => {
        const outcomeDate = new Date(outcome.outcomeDetails.startDate);
        const currentDate = new Date();

        const bet: AnalyticsBet = {
            label: outcomeDate < currentDate ? BetLabel.Prematch : BetLabel.Live,
            group: kombi ? KombiGroup[outcome.group] : Constants.NotApplied,
            sportLeague: outcome.outcomeDetails.typeName,
            matchName: outcome.outcomeDetails.eventName,
            quote: outcome.outcomeDetails.prices[0].decimal.toString(),
            gameCode: outcome.outcomeDetails?.categoryName,
            betType: outcome.outcomeDetails?.marketName,
        };

        return bet;
    };

    const transformTicketToAnalyticsTicketObj = (
        ticket: BetData,
        includePrices: boolean = true,
        cashoutableOnly: boolean = false,
    ): AnalyticsTicket => {
        const outcomes = ticket.legs
            .map((leg) =>
                leg.outcomes
                    .filter((outcome) => !cashoutableOnly || !outcome.outcomeDetails.result)
                    .map((outcome) => ({ ...outcome, group: leg.options.group })),
            )
            .flat();

        const bets = outcomes.map((outcome) => {
            return getAnalyticsTicketBetFromPlacedTicket(outcome, ticket.name === "Kombi");
        });

        let additionalWinningsValue;

        if (includePrices) {
            if (ticket.bonusInfo) {
                additionalWinningsValue = ticket.bonusInfo?.monetaryValue;
            } else {
                additionalWinningsValue = Constants.NoAdditionalWinnings;
            }
        } else {
            additionalWinningsValue = undefined;
        }

        const analyticsTicket: AnalyticsTicket = {
            id: ticket.id,
            totalOdd: Number(ticket.potentialPrice.decimal).toFixed(2),
            totalDeposit: includePrices ? ticket.stake : undefined,
            additionalWinnings: additionalWinningsValue,
            potentialWinnings: includePrices ? Number(ticket?.potentialPayout).toFixed(2) : undefined,
            type: ticket.name,
            betCount: ticket.legs.length,
            bets,
        };

        return analyticsTicket;
    };

    const getLowestMarketPrices = (markets: EventsDrilldownDetailEventMarket[]) => {
        const lowestDisplayorderMarket = markets?.reduce(
            (acc, curr) => (acc.displayOrder < curr.displayOrder ? acc : curr),
            markets[0] ?? ({} as EventsDrilldownDetailEventMarket),
        );

        // Sort the outcomes by subType: H first, D second, A third, everything else last
        const sortOutcomesBySubType = (a, b) => {
            const subTypeOrder = { H: 0, D: 1, A: 2 };
            const aOrder = subTypeOrder[a.subType] !== undefined ? subTypeOrder[a.subType] : 999;
            const bOrder = subTypeOrder[b.subType] !== undefined ? subTypeOrder[b.subType] : 999;
            return aOrder - bOrder;
        };

        // Get prices in correct order
        return lowestDisplayorderMarket?.outcomes
            ?.toSorted(sortOutcomesBySubType)
            ?.map((outcome) => outcome.prices?.[0]?.decimal)
            .filter(Boolean)
            .join(", ");
    };

    const renameTicketSettings = (settings: PriceChangeBehaviour) => {
        switch (settings) {
            case PriceChangeBehaviour.ALL:
                return "acceptAll";
            case PriceChangeBehaviour.NONE:
                return "rejectAll";
            case PriceChangeBehaviour.HIGHER:
                return "acceptHigher";
            default:
                return settings;
        }
    };

    return {
        setPageAnalytics,
        setTicketBetsGeneral,
        setTicketGeneral,
        setAnalyticsFavouriteSports,
        getTicketBet,
        transformTicketToAnalyticsTicketObj,
        renameTicketSettings,
        getLowestMarketPrices,
    };
}
